import React, { useState } from 'react';
import PageHeader from '../components/PageHeader';
import { Link } from 'react-router-dom';
import { ShieldCheck, CheckCircle2, AlertCircle } from 'lucide-react';

const DoNotSell = () => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', state: '', requestType: 'opt-out' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email is required';
    if (!form.state) e.state = 'Please select your state';
    return e;
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((er) => ({ ...er, [e.target.name]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    setTimeout(() => { setLoading(false); setSubmitted(true); }, 1200);
  };

  const usStates = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware',
    'Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky',
    'Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi',
    'Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico',
    'New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania',
    'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
    'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
  ];

  return (
    <div className="page-enter">
      <PageHeader title="Do Not Sell My Personal Information" breadcrumbs={[{ label: 'Do Not Sell My Personal Information' }]} />

      <div className="py-14 bg-background">
        <div className="max-w-4xl mx-auto px-4">

          {/* Intro Banner */}
          <div className="flex items-start gap-4 bg-muted border border-border p-6 mb-10 rounded-sm">
            <ShieldCheck size={36} style={{ color: '#43a047' }} className="flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-bold text-foreground mb-1">Your Privacy Rights Matter to Us</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Under the <strong>California Consumer Privacy Act (CCPA)</strong> and other applicable state privacy laws, you have the right to opt out of the sale or sharing of your personal information. Use the form below to submit your request and we will process it within <strong>15 business days</strong>.
              </p>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-10">

            {/* Form */}
            <div className="flex-1">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center bg-muted border border-border rounded-sm">
                  <CheckCircle2 size={52} style={{ color: '#43a047' }} className="mb-4" />
                  <h3 className="text-xl font-bold text-foreground mb-2">Request Submitted</h3>
                  <p className="text-muted-foreground text-sm max-w-sm mb-2">
                    We've received your request and will process it within <strong>15 business days</strong>.
                  </p>
                  <p className="text-muted-foreground text-xs mb-6">A confirmation has been sent to <strong>{form.email}</strong>.</p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ firstName: '', lastName: '', email: '', phone: '', state: '', requestType: 'opt-out' }); }}
                    style={{ backgroundColor: '#43a047' }}
                    className="text-white text-sm font-medium px-6 py-2.5 rounded-sm hover:opacity-90 transition-opacity"
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <h3 className="text-lg font-bold text-foreground mb-4">Submit Your Privacy Request</h3>

                  {/* Request Type */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Request Type *</label>
                    <div className="space-y-2">
                      {[
                        { value: 'opt-out', label: 'Opt Out of Sale/Sharing of My Personal Information' },
                        { value: 'access', label: 'Access / Know What Personal Information Is Collected' },
                        { value: 'delete', label: 'Delete My Personal Information' },
                        { value: 'correct', label: 'Correct My Personal Information' },
                      ].map((opt) => (
                        <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="requestType"
                            value={opt.value}
                            checked={form.requestType === opt.value}
                            onChange={handleChange}
                            className="accent-[#43a047] w-4 h-4"
                          />
                          <span className="text-sm text-muted-foreground">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Name */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        className={`w-full border px-3 py-2.5 text-sm rounded-sm focus:outline-none transition-colors ${errors.firstName ? 'border-red-400 focus:border-red-400' : 'border-border focus:border-[#43a047]'}`}
                        placeholder="First name"
                      />
                      {errors.firstName && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.firstName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        className={`w-full border px-3 py-2.5 text-sm rounded-sm focus:outline-none transition-colors ${errors.lastName ? 'border-red-400 focus:border-red-400' : 'border-border focus:border-[#43a047]'}`}
                        placeholder="Last name"
                      />
                      {errors.lastName && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.lastName}</p>}
                    </div>
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        className={`w-full border px-3 py-2.5 text-sm rounded-sm focus:outline-none transition-colors ${errors.email ? 'border-red-400 focus:border-red-400' : 'border-border focus:border-[#43a047]'}`}
                        placeholder="your@email.com"
                      />
                      {errors.email && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className="w-full border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#43a047] transition-colors"
                        placeholder="+1 (xxx) xxx-xxxx"
                      />
                    </div>
                  </div>

                  {/* State */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">State of Residence *</label>
                    <select
                      name="state"
                      value={form.state}
                      onChange={handleChange}
                      className={`w-full border px-3 py-2.5 text-sm rounded-sm focus:outline-none transition-colors bg-white ${errors.state ? 'border-red-400 focus:border-red-400' : 'border-border focus:border-[#43a047]'}`}
                    >
                      <option value="">Select your state...</option>
                      {usStates.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {errors.state && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle size={11} />{errors.state}</p>}
                  </div>

                  <p className="text-xs text-muted-foreground leading-relaxed">
                    By submitting this form, you are exercising your rights under applicable privacy laws. We will verify your identity before processing your request. See our <Link to="/privacy-policy" style={{ color: '#43a047' }} className="hover:underline">Privacy Policy</Link> for more information.
                  </p>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{ backgroundColor: '#43a047' }}
                    className="inline-flex items-center gap-2 text-white font-medium px-8 py-2.5 rounded-sm hover:opacity-90 transition-opacity text-sm disabled:opacity-60"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      <><ShieldCheck size={15} /> Submit Privacy Request</>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:w-72 space-y-5">
              <div className="bg-muted border border-border p-5">
                <h4 className="font-bold text-foreground mb-4 text-sm uppercase tracking-wide">Your Rights Include</h4>
                <ul className="space-y-3">
                  {[
                    { title: 'Right to Know', desc: 'Know what personal info we collect and how it\'s used.' },
                    { title: 'Right to Delete', desc: 'Request deletion of your personal information.' },
                    { title: 'Right to Opt Out', desc: 'Opt out of the sale or sharing of your data.' },
                    { title: 'Right to Correct', desc: 'Request correction of inaccurate information.' },
                    { title: 'Non-Discrimination', desc: 'We will not discriminate for exercising your rights.' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <CheckCircle2 size={15} style={{ color: '#43a047' }} className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-foreground">{item.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-muted border border-border p-5">
                <h4 className="font-bold text-foreground mb-3 text-sm uppercase tracking-wide">Response Timeline</h4>
                <p className="text-xs text-muted-foreground leading-relaxed mb-2">We will respond to your request within <strong>15 business days</strong> of receipt.</p>
                <p className="text-xs text-muted-foreground leading-relaxed">For complex requests, we may extend our response time by an additional <strong>45 days</strong> and will notify you accordingly.</p>
              </div>

              <div className="bg-muted border border-border p-5">
                <h4 className="font-bold text-foreground mb-3 text-sm uppercase tracking-wide">Authorized Agent</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">You may use an authorized agent to submit a request on your behalf. Contact us directly for agent authorization requirements.</p>
                <a href="mailto:info@b2btechleads.com" style={{ color: '#43a047' }} className="text-xs font-medium hover:underline mt-2 block">
                  info@b2btechleads.com
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default DoNotSell;
